import { useEffect, useMemo } from 'react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { InsuranceQueries } from '@frontend/api-insurance';
import { useGetInsuranceStatusesStoreShallow, useInsuranceTabShallowStore } from '../stores';
import { getVerificationCountsRequestByLocationIds } from '../utils/appointment-helper';
import { StatusSelectionLabel } from '../utils/types';

type UseGetInsuranceStatusesType = {
  appointments: Appointment[] | CalendarEvent[];
  locationId?: string;
  isSingleLocationView?: boolean;
  scheduleSelectedLocationIds?: string[];
  isCalendarView?: boolean;
  calendarDate?: string;
};

export const useGetInsuranceStatuses = ({
  locationId,
  appointments,
  isSingleLocationView = false,
  scheduleSelectedLocationIds,
  isCalendarView = false,
  calendarDate = '',
}: UseGetInsuranceStatusesType) => {
  const locationIds = useMemo(() => {
    return isSingleLocationView && locationId ? [locationId] : scheduleSelectedLocationIds || [];
  }, [isSingleLocationView, locationId, scheduleSelectedLocationIds]);

  const { setInsuranceStatusCounts, setInsuranceStatusByPersonIdsMap, setIsLoading, setRefetchInsuranceStatuses } =
    useInsuranceTabShallowStore(
      'setInsuranceStatusByPersonIdsMap',
      'setInsuranceStatusCounts',
      'setIsLoading',
      'setRefetchInsuranceStatuses'
    );

  const { setInsuranceStatuses } = useGetInsuranceStatusesStoreShallow('setInsuranceStatuses');

  const checkInsuranceCountRequest = useMemo(() => {
    return getVerificationCountsRequestByLocationIds(appointments || [], locationIds);
  }, [appointments, locationIds]);

  const {
    data: insuranceStatuses,
    isLoading: isLoadingInsuranceStatuses,
    refetch,
    isRefetching: isRefetchingInsuranceStatuses,
  } = InsuranceQueries.useGetCheckInsuranceCount(checkInsuranceCountRequest);

  // Store the refetch function in the store to allow manual refetching
  useEffect(() => {
    if (locationIds.length && appointments.length) {
      setRefetchInsuranceStatuses(refetch);
    }
  }, [appointments, locationIds, refetch, setRefetchInsuranceStatuses]);

  // useEffect to set loading state based on isLoading and isRefetching
  useEffect(() => {
    setIsLoading(isLoadingInsuranceStatuses || isRefetchingInsuranceStatuses);
  }, [isLoadingInsuranceStatuses, isRefetchingInsuranceStatuses, setIsLoading]);

  // Stores insuranceStatues in the store when in calendar view and calendarDate is available
  useEffect(() => {
    if (insuranceStatuses && isCalendarView && calendarDate) {
      //set the insuranceStatuses in the store
      setInsuranceStatuses(calendarDate, insuranceStatuses);
    }
  }, [insuranceStatuses, isCalendarView, calendarDate, setInsuranceStatuses]);

  useEffect(() => {
    const hasInsuranceStatus = !!insuranceStatuses;
    const isFetching = isRefetchingInsuranceStatuses || isLoadingInsuranceStatuses;

    if (!isFetching && locationId) {
      const coverageStatusByPersonId = hasInsuranceStatus
        ? insuranceStatuses?.locationVerificationCounts?.[locationId]?.coverageStatusByPersonId
        : {};

      const verificationAnalysis = hasInsuranceStatus
        ? insuranceStatuses?.locationVerificationCounts?.[locationId]?.verificationAnalysis
        : {};

      setInsuranceStatusByPersonIdsMap(coverageStatusByPersonId || {});
      setInsuranceStatusCounts(verificationAnalysis || {});
    }
  }, [
    insuranceStatuses,
    isLoadingInsuranceStatuses,
    isRefetchingInsuranceStatuses,
    locationId,
    setInsuranceStatusByPersonIdsMap,
    setInsuranceStatusCounts,
    setInsuranceStatuses,
  ]);

  const count = useMemo(() => {
    if (!insuranceStatuses || isLoadingInsuranceStatuses) return 0;
    return locationIds.reduce((sum, locationId) => {
      const analysis = insuranceStatuses.locationVerificationCounts?.[locationId]?.verificationAnalysis || {};
      return sum + (analysis?.[StatusSelectionLabel.Unverified] || 0) + (analysis?.[StatusSelectionLabel.Error] || 0);
    }, 0);
  }, [insuranceStatuses, isLoadingInsuranceStatuses, locationIds]);

  return {
    calendarInsuranceCount: count,
    isLoading: isLoadingInsuranceStatuses || isRefetchingInsuranceStatuses,
    insuranceStatuses,
  };
};
